import { useEffect, useState } from 'react'

import { api } from '../../api/client'
import type { PatchSlaPolicy } from '../../api/types'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { Card, ErrorBanner, Spinner } from '../common'
import { severityLabel, useIsTenantAdmin } from './labels'

const SEVERITA = ['critica', 'alta', 'media', 'bassa'] as const

/**
 * The tenant's SLA policy (patch_sla_service): within how many days a missing
 * patch must be installed, by severity, for exploited patches (KEV) and per
 * environment. Fleet, catalogue, compliance, reports and lifecycle read it.
 */
export function SlaPolicy({ onSaved }: { onSaved?: () => void }) {
  const state = useAsync(() => api.patchSla(), [])
  const admin = useIsTenantAdmin()
  const [v, setV] = useState<PatchSlaPolicy | null>(null)
  const [righe, setRighe] = useState<{ nome: string; giorni: Record<string, string> }[]>([])
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [fatto, setFatto] = useState(false)
  const d = state.data

  useEffect(() => {
    if (!d) return
    setV(d.policy)
    setRighe(Object.entries(d.policy.environments).map(([nome, g]) => ({
      nome, giorni: Object.fromEntries(SEVERITA.map((s) => [s, g[s] != null ? String(g[s]) : ''])),
    })))
  }, [d])

  if (state.error) return <ErrorBanner error={state.error} />
  if (!d || !v) return <Spinner />

  const salva = async () => {
    setLavoro(true)
    setErrore(null)
    setFatto(false)
    try {
      const environments: Record<string, Record<string, number>> = {}
      for (const r of righe) {
        const nome = r.nome.trim()
        if (!nome) continue
        const g: Record<string, number> = {}
        for (const s of SEVERITA) if (r.giorni[s]?.trim()) g[s] = Number(r.giorni[s])
        if (Object.keys(g).length) environments[nome] = g
      }
      await api.patchSlaSave({ ...v, environments })
      setFatto(true)
      state.reload()
      onSaved?.()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }
  const nomiUsati = new Set(righe.map((r) => r.nome))

  return (
    <Card title={t('Politica SLA delle patch')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Entro quanti giorni una patch mancante va installata. Parco, catalogo, conformità per ambiente, report e ciclo di vita leggono questa politica: cambiarla cambia subito tutte le viste, anche la storia (l’età di una patch non cambia, la sua scadenza sì).')}
      </p>
      <ErrorBanner error={errore} />
      {fatto && <div className="banner banner--ok">{t('Politica salvata.')}</div>}
      <form className="form-dialog" onSubmit={(e) => { e.preventDefault(); void salva() }}>
        <h4 className="form__sezione">{t('Per gravità')}</h4>
        <div className="form-grid">
          {SEVERITA.map((s) => (
            <label key={s} className="field span-1"><span className="field__label">{severityLabel(s)}</span>
              <input type="number" min={1} max={730} value={v.days[s] ?? ''} disabled={!admin}
                     onChange={(e) => setV({ ...v, days: { ...v.days, [s]: Number(e.target.value) } })} required /></label>
          ))}
          <label className="field span-2"><span className="field__label">{t('Patch sfruttate (CISA KEV), al più')}</span>
            <input type="number" min={1} max={730} value={v.kev_days ?? ''} disabled={!admin} placeholder={t('nessuna regola')}
                   onChange={(e) => setV({ ...v, kev_days: e.target.value ? Number(e.target.value) : null })} /></label>
        </div>
        <h4 className="form__sezione">{t('Per ambiente (tag «{chiave}»)', { chiave: d.environment_key })}</h4>
        <p className="field__hint" style={{ marginTop: 0 }}>{t('Solo i giorni che cambiano: un campo vuoto vale come sopra.')}</p>
        {righe.map((r, i) => (
          <div key={i} className="form-grid">
            <label className="field span-2"><span className="field__label">{t('Ambiente')}</span>
              <input type="text" list="sla-ambienti" value={r.nome} disabled={!admin}
                     onChange={(e) => setRighe(righe.map((x, j) => (j === i ? { ...x, nome: e.target.value } : x)))} /></label>
            {SEVERITA.map((s) => (
              <label key={s} className="field span-1"><span className="field__label">{severityLabel(s)}</span>
                <input type="number" min={1} max={730} value={r.giorni[s] ?? ''} disabled={!admin}
                       onChange={(e) => setRighe(righe.map((x, j) => (j === i ? { ...x, giorni: { ...x.giorni, [s]: e.target.value } } : x)))} /></label>
            ))}
            {admin && (
              <div className="field span-1" style={{ alignSelf: 'end' }}>
                <button type="button" className="link" onClick={() => setRighe(righe.filter((_, j) => j !== i))}>{t('togli')}</button>
              </div>
            )}
          </div>
        ))}
        <datalist id="sla-ambienti">
          {d.environment_values.filter((x) => !nomiUsati.has(x)).map((x) => <option key={x} value={x} />)}
        </datalist>
        {admin && (
          <div className="form-dialog__azioni" style={{ justifyContent: 'space-between' }}>
            <button type="button" className="secondary" onClick={() => setRighe([...righe, { nome: '', giorni: {} }])}>{t('Aggiungi un ambiente')}</button>
            <button type="submit" disabled={lavoro}>{lavoro ? t('Salvo…') : t('Salva')}</button>
          </div>
        )}
        {!admin && <p className="field__hint">{t('Sola lettura: la politica la cambia l’amministratore del tenant.')}</p>}
      </form>
    </Card>
  )
}
